import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import {
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_LIVE_REVIEW_WAITING,
  STRATEGY_CANVAS_VIEW,
} from "../../../../data/RouteUrls";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronRightIcon,
  CommentIcon,
  InfoIcon,
  LockIcon,
  StatusPill,
} from "../StrategyCanvasHelpers";
import CanvasBand, { CanvasGroup } from "../CanvasBand/CanvasBand";
import {
  buildBands,
  getBlockLabel,
  useInitialCanvasFocus,
} from "../StrategyCanvasStructure";
import SummaryGroups from "../SummaryPanel/SummaryPanel";
import {
  ITEM_KIND,
  buildAgendaGroups,
  countItems,
  getOpenRemarksByBlock,
  groupLiveReviewRemarks,
} from "../StrategyCanvasSummary";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "../_redux/StrategyCanvasStore";
import useStrategyCanvasReviewStore, {
  useStrategyCanvasReviewStoreSelector,
} from "../StrategyCanvasReview/_redux/StrategyCanvasReviewStore";
import { REVIEW_STATUS } from "../StrategyCanvasReview/_redux/StrategyCanvasReviewUrls";
import { MODE_REVIEW, MODE_CANVAS } from "../StrategyCanvasReview/StrategyCanvasReview";
import StrategyCanvasCanvasMode from "../StrategyCanvasReview/CanvasMode/StrategyCanvasCanvasMode";
import {
  GrowthDriversSummary,
  parseGrowthDrivers,
} from "../StrategyCanvasReview/ReviewMode/ReviewRow";
import LiveReviewInfoModal from "./LiveReviewInfoModal/LiveReviewInfoModal";
import ActionConfirmModal from "./ActionConfirmModal/ActionConfirmModal";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
import "./StrategyCanvasLiveReviewWaiting.scss";

const selectClients = (state) => state.clients;
const selectLiveReviewStatusByUniqueId = (state) => state.liveReviewStatusByUniqueId;
const selectLiveReviewStatusLoading = (state) => state.liveReviewStatusLoading;
const selectStartingLiveReview = (state) => state.startingLiveReview;
const selectLiveReviewByUniqueId = (state) => state.liveReviewByUniqueId;
const selectLiveReviewLoading = (state) => state.liveReviewLoading;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;
const selectReviewLoading = (state) => state.reviewLoading;
const selectEditingLiveReviewBlockKey = (state) => state.editingLiveReviewBlockKey;
const selectSettingLiveReviewBlockStatus = (state) => state.settingLiveReviewBlockStatus;
const selectLockingCanvas = (state) => state.lockingCanvas;

const LIVE_REVIEW_IN_PROGRESS = "LIVE_REVIEW_IN_PROGRESS";

// Persisted the same way as the "how review works" explainer flag in
// StrategyCanvasReview.js - marks that this coach has already seen the
// "how live review works" explainer once, so it only auto-opens the very
// first time a live review session starts, not on every visit.
const LIVE_REVIEW_INFO_SEEN_KEY = "scLiveReviewInfoSeen";

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 7.5L6 4L9.5 7.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path
      d="M9.5 2.5 11.5 4.5 4.5 11.5H2.5V9.5L9.5 2.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DiscardIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path
      d="M3 3L11 11M3 11L11 3"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

// Editable counterpart to Review Mode's read-only GrowthDriversSummary
// (ReviewRow.js) - same layout (timeframe pill, Revenue/Profit rows, one row
// per driver). Revenue and Profit are fixed metrics, so their labels stay
// plain text and only their start/end values are editable. The 3 growth
// drivers are free-form, so their label (name) is editable as well as their
// start/end values. Timeframe and units are not editable.
//
// `showNameErrors` is only turned on after a blocked Save, so a blank name is
// not flagged while the coach is still mid-edit; once on, each error clears the
// moment its name is typed.
const GrowthDriversEditor = ({ data, onChange, showNameErrors = false }) => {
  const handleStatChange = (key, field, rawValue) => {
    const value = rawValue === "" ? "" : Number(rawValue);
    onChange({ ...data, [key]: { ...data[key], [field]: value } });
  };

  const handleDriverValueChange = (index, field, rawValue) => {
    const value = rawValue === "" ? "" : Number(rawValue);
    onChange({
      ...data,
      drivers: data.drivers.map((driver, driverIndex) =>
        driverIndex === index ? { ...driver, [field]: value } : driver,
      ),
    });
  };

  const handleDriverNameChange = (index, name) => {
    onChange({
      ...data,
      drivers: data.drivers.map((driver, driverIndex) =>
        driverIndex === index ? { ...driver, name } : driver,
      ),
    });
  };

  return (
    <div className="rr-growth">
      {data.timeframe && (
        <div className="rr-growth-timeframe">
          <span>{data.timeframe.start || "—"}</span>
          <ArrowRightIcon />
          <span>{data.timeframe.end || "—"}</span>
        </div>
      )}

      <div className="rr-growth-drivers">
        {[
          { label: "Revenue", key: "revenue" },
          { label: "Profit", key: "profit" },
        ].map(({ label, key }) => (
          <div className="rr-growth-driver" key={key}>
            <span className="rr-growth-driver-name">{label}</span>
            <div className="lrs-growth-value-inputs">
              <input
                type="number"
                className="lrs-growth-value-input"
                value={data[key]?.start ?? ""}
                onChange={(event) => handleStatChange(key, "start", event.target.value)}
              />
              <ArrowRightIcon />
              <input
                type="number"
                className="lrs-growth-value-input"
                value={data[key]?.end ?? ""}
                onChange={(event) => handleStatChange(key, "end", event.target.value)}
              />
            </div>
          </div>
        ))}

        {data.drivers?.map((driver, index) => {
          const nameInvalid = showNameErrors && !(driver.name || "").trim();
          return (
          <div className="rr-growth-driver" key={`driver-${index}`}>
            <div className="lrs-growth-name-field">
              <input
                type="text"
                className={`lrs-growth-name-input${nameInvalid ? " is-invalid" : ""}`}
                value={driver.name ?? ""}
                placeholder={`Driver ${index + 1}`}
                aria-label={`Driver ${index + 1} name`}
                aria-invalid={nameInvalid || undefined}
                aria-describedby={nameInvalid ? `lrs-growth-name-error-${index}` : undefined}
                onChange={(event) => handleDriverNameChange(index, event.target.value)}
              />
              {nameInvalid && (
                <span
                  className="lrs-growth-name-error"
                  id={`lrs-growth-name-error-${index}`}
                  role="alert"
                >
                  Enter a name for this driver.
                </span>
              )}
            </div>
            <div className="lrs-growth-value-inputs">
              <input
                type="number"
                className="lrs-growth-value-input"
                value={driver.start ?? ""}
                onChange={(event) => handleDriverValueChange(index, "start", event.target.value)}
              />
              <ArrowRightIcon />
              <input
                type="number"
                className="lrs-growth-value-input"
                value={driver.end ?? ""}
                onChange={(event) => handleDriverValueChange(index, "end", event.target.value)}
              />
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

// Figma node 33450-155934/155973/155995 - a Live Review row: Accept/Edit
// while it isn't accepted yet, Edit-only once it is, and any flagged
// comments for the block shown as read-only transcript boxes underneath.
// Clicking Edit swaps the value for a textarea with explicit Save/Discard
// buttons (no auto-save-on-blur) - or, for growth_drivers, the structured
// GrowthDriversEditor above instead of a raw-JSON textarea.
export const LiveReviewRow = ({
  block,
  expanded,
  onToggle,
  disabled,
  briefId,
  // Agenda items still open against this block. Empty once they're all
  // discussed, or when the block was never flagged in the first place.
  openRemarks = [],
}) => {
  const [markingComplete, setMarkingComplete] = useState(false);
  // Covers the whole finalise operation. The store's own `accepting` flag only
  // spans the status call, so without this the button would flick back to
  // enabled while the remarks were still being cleared.
  const [finalising, setFinalising] = useState(false);
  const isGrowthDrivers = block.block_key === "growth_drivers";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.value || "");
  const [growthDraft, setGrowthDraft] = useState(() =>
    isGrowthDrivers ? parseGrowthDrivers(block.value) : null,
  );
  // Set by a blocked Save, cleared on edit/discard — see `handleSave`.
  const [showNameErrors, setShowNameErrors] = useState(false);

  const editingBlockKey = useStrategyCanvasReviewStoreSelector(selectEditingLiveReviewBlockKey);
  const settingStatusKey = useStrategyCanvasReviewStoreSelector(
    selectSettingLiveReviewBlockStatus,
  );
  const saving = editingBlockKey === block.block_key;
  const accepting = settingStatusKey === block.block_key;

  // The 7.4 payload's blocks use "status" (not "review_status" - that's
  // the 5.2/5.3 review payload's field name), same "accepted"/"to_discuss"
  // strings as REVIEW_STATUS either way.
  const status = block.status;
  const comments = block.comments || [];

  // Sequentially, not Promise.all: the store keeps a single
  // discussingRemarkKey and refuses a second call while one is in flight, so
  // firing them together would silently drop all but the first.
  const markOpenRemarksDiscussed = async () => {
    for (const remark of openRemarks) {
      // eslint-disable-next-line no-await-in-loop
      await useStrategyCanvasReviewStore
        .getState()
        .markRemarkDiscussed(briefId, remark.remark_type, remark.id);
    }
  };

  // Only for a block that arrived already finalised but still carries an open
  // remark - accepted-with-a-comment from the solo review. A block flagged to
  // discuss clears its own remarks when it's finalised, so it never needs this.
  const canMarkComplete =
    status === REVIEW_STATUS.ACCEPTED &&
    comments.length > 0 &&
    openRemarks.length > 0;

  const handleMarkComplete = async () => {
    if (markingComplete) return;
    setMarkingComplete(true);
    await markOpenRemarksDiscussed();
    setMarkingComplete(false);
  };

  const startEditing = () => {
    setDraft(block.value || "");
    if (isGrowthDrivers) setGrowthDraft(parseGrowthDrivers(block.value));
    setShowNameErrors(false);
    setEditing(true);
  };

  const handleDiscard = () => {
    setDraft(block.value || "");
    if (isGrowthDrivers) setGrowthDraft(parseGrowthDrivers(block.value));
    setShowNameErrors(false);
    setEditing(false);
  };

  const handleSave = async () => {
    if (isGrowthDrivers) {
      if (growthDraft) {
        // A driver must have a name. Save stays clickable and the check runs
        // here (the same call the Complete Review gate makes) so the coach is
        // shown WHICH driver is blank, rather than a dead disabled button. The
        // editor stays open and nothing is written.
        const hasBlankName = (growthDraft.drivers || []).some(
          (driver) => !(driver.name || "").trim(),
        );
        if (hasBlankName) {
          setShowNameErrors(true);
          return;
        }
        // Trim edited driver labels before saving so stray whitespace
        // never ends up as a driver name.
        const cleaned = {
          ...growthDraft,
          drivers: (growthDraft.drivers || []).map((driver) => ({
            ...driver,
            name: (driver.name || "").trim(),
          })),
        };
        const serialized = JSON.stringify(cleaned);
        if (serialized !== (block.value || "")) {
          await useStrategyCanvasReviewStore
            .getState()
            .editLiveReviewBlock(briefId, block.block_key, serialized);
        }
      }
      setEditing(false);
      return;
    }

    const trimmed = draft.trim();
    if (trimmed && trimmed !== (block.value || "").trim()) {
      await useStrategyCanvasReviewStore.getState().editLiveReviewBlock(briefId, block.block_key, trimmed);
    }
    setEditing(false);
  };

  // Finalising settles the block *and* whatever the agenda still had open
  // against it - agreeing the wording in the room is the discussion, so
  // leaving the remark open would make the coach clear the same thing twice
  // and hold up Lock Canvas until they did.
  const handleAccept = async () => {
    if (accepting || finalising) return;
    setFinalising(true);
    await useStrategyCanvasReviewStore
      .getState()
      .setLiveReviewBlockStatus(briefId, block.block_key, REVIEW_STATUS.ACCEPTED);
    await markOpenRemarksDiscussed();
    setFinalising(false);
  };

  return (
    <div
      className={`lrs-row ${expanded ? "expanded" : ""} ${disabled ? "disabled" : ""}`}
      data-agenda-target={`block:${block.block_key}`}
    >
      <button
        type="button"
        className="lrs-row-header"
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
      >
        <span className="lrs-row-left">
          <span className="lrs-row-label">{getBlockLabel(block)}</span>
        </span>
        <span className="lrs-row-right">
          {status && (
            <StatusPill
              accepted={status === REVIEW_STATUS.ACCEPTED}
              className="lrs-status-pill"
              acceptedClassName="lrs-status-accepted"
              discussClassName="lrs-status-discuss"
            />
          )}
          {!disabled && (
            <span className="lrs-row-chevron">{expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
          )}
        </span>
      </button>
      {expanded && !disabled && (
        <div className="lrs-row-body">
          {editing ? (
            <div className="lrs-row-editor">
              {isGrowthDrivers ? (
                growthDraft && (
                  <GrowthDriversEditor
                    data={growthDraft}
                    onChange={setGrowthDraft}
                    showNameErrors={showNameErrors}
                  />
                )
              ) : (
                <textarea
                  className="lrs-row-textarea"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={saving}
                  autoFocus
                />
              )}
              <div className="lrs-row-editor-actions">
                <button
                  type="button"
                  className="lrs-action-btn lrs-discard"
                  onClick={handleDiscard}
                  disabled={saving}
                >
                  <DiscardIcon />
                  Discard
                </button>
                <button
                  type="button"
                  className="lrs-action-btn lrs-save"
                  onClick={handleSave}
                  disabled={saving || (!isGrowthDrivers && !draft.trim())}
                >
                  <CheckIcon size={12} />
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <>
              {isGrowthDrivers ? (
                <GrowthDriversSummary value={block.value} />
              ) : (
                <p className="lrs-row-value-text">{block.value || "Not added yet"}</p>
              )}
              {/* "Finalise" rather than "Accept": in the live session this is
                  the coach and CEO agreeing the block's wording together, not
                  the coach signing off on someone else's work. */}
              <div className="lrs-row-actions">
                {status !== REVIEW_STATUS.ACCEPTED && (
                  <button
                    type="button"
                    className="lrs-action-btn lrs-accept"
                    onClick={handleAccept}
                    disabled={accepting || finalising}
                  >
                    <CheckIcon size={12} />
                    {accepting || finalising
                      ? "Finalising…"
                      : "Finalise this block"}
                  </button>
                )}
                {canMarkComplete && (
                  <button
                    type="button"
                    className="lrs-action-btn lrs-complete"
                    onClick={handleMarkComplete}
                    disabled={markingComplete}
                  >
                    <CheckIcon size={12} />
                    {markingComplete ? "Marking…" : "Mark as discussed"}
                  </button>
                )}
                <button type="button" className="lrs-action-btn lrs-edit" onClick={startEditing}>
                  <EditIcon />
                  Modify
                </button>
              </div>
              {comments.length > 0 && (
                <div className="lrs-transcripts">
                  {comments.map((comment) => (
                    <div className="lrs-transcript-box" key={comment.coach_comment_id}>
                      <span className="lrs-transcript-badge">
                        <CommentIcon size={11} />
                        Your comment
                      </span>
                      <p>{comment.comment_text}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// The agenda list itself now comes from SummaryPanel.js, rendering the same
// band > section grouping as the Review Summary. Section 7.6 (mark a remark
// Discussed) is wired through its circle via handleToggleDiscussed below —
// still one item per remark, so remark_type + id semantics are unchanged.

// Shimmer placeholder shown while status/review/live-review data is still
// loading - mirrors the real .lrs-body layout (section headings + rows on
// the left, a "Meeting Agenda" panel on the right) instead of a blank
// "Loading..." line.
const LiveReviewSkeleton = () => (
  <div className="lrs-body">
    <div className="lrs-canvas">
      {Array.from({ length: 2 }).map((_, sectionIndex) => (
        <div className="lrs-section" key={sectionIndex}>
          <div className="lrs-section-heading">
            <span className="lrs-skel lrs-skel-bar" />
            <span className="lrs-skel lrs-skel-title" />
          </div>
          <div className="lrs-rows">
            {Array.from({ length: 4 }).map((__, rowIndex) => (
              <span className="lrs-skel lrs-skel-row" key={rowIndex} />
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="lrs-remarks">
      <div className="lrs-remarks-header">
        <span className="lrs-skel lrs-skel-remarks-title" />
      </div>
      <div className="lrs-remarks-divider" />
      <div className="lrs-remarks-body">
        {Array.from({ length: 3 }).map((_, index) => (
          <span className="lrs-skel lrs-skel-remark-card" key={index} />
        ))}
      </div>
    </div>
  </div>
);

// Blocks in a band still waiting to be finalised. Counts anything not yet
// accepted, not just the ones flagged to_discuss, so the band stat and the
// Lock Canvas gate can never disagree about what's left.
const countOpenBlocks = (band) =>
  band.sections
    .flatMap((section) => section.groups)
    .flatMap((group) => group.blocks)
    .filter(
      (block) =>
        (block.status || block.review_status) !== REVIEW_STATUS.ACCEPTED,
    ).length;

// Shared body layout - canvas (left) + "Meeting Agenda" panel (right). Used
// both before Live Review has started (read-only, from 5.2 review data, no
// remarks fetched yet) and once it's in progress (interactive, from 7.4).
const LiveReviewBody = ({ briefId, layers, remarks, interactive, onLockCanvas, lockingCanvas }) => {
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  // Agenda item the coach just clicked, waiting to be scrolled to once the
  // band holding it has actually rendered.
  const [pendingTarget, setPendingTarget] = useState(null);
  const canvasRef = useRef(null);

  // Same 4-band grouping the canvas and Review Mode use - see
  // StrategyCanvasStructure.js. This body renders both the pre-start
  // read-only view (5.2 data, blocks carry `review_status`) and the live
  // session (7.4 data, blocks carry `status`), so band progress reads
  // whichever field is present.
  const bands = useMemo(
    () => buildBands(layers, (block) => block.status || block.review_status),
    [layers],
  );

  // Session opens on Business DNA with its first block expanded and the other
  // bands folded away, matching where Review Mode starts.
  const {
    collapsedBands,
    setCollapsedBands,
    expandedBlockKey,
    setExpandedBlockKey,
  } = useInitialCanvasFocus(bands);

  const toggleBand = (bandKey) =>
    setCollapsedBands((prev) => {
      const next = new Set(prev);
      if (next.has(bandKey)) next.delete(bandKey);
      else next.add(bandKey);
      return next;
    });

  // Both halves have to be settled. Every block finalised covers the ones that
  // were never flagged; every agenda item discussed covers the ones that were.
  // Neither alone is enough - blocks with no remark would slip through the
  // agenda check, and a finalised block can still have an open remark against
  // it. Both are cleared from the canvas on the left.
  const allBlocks = layers.flatMap((layer) => layer.blocks);
  const unfinalisedCount = allBlocks.filter(
    (block) => block.status !== REVIEW_STATUS.ACCEPTED,
  ).length;
  const openRemarkCount = remarks.filter((remark) => !remark.discussed).length;
  const canLockCanvas = unfinalisedCount === 0 && openRemarkCount === 0;

  // Explains why Lock Canvas is disabled, whenever it actually is - empty once
  // it's clickable, so the tooltip wrapper below just renders a plain button in
  // that case.
  //
  // No counts: mid-session the coach is working through the canvas, and a
  // number that ticks down invites them to chase it rather than attend to the
  // conversation. The band headers and the agenda already show what's left, so
  // this only needs to say what unlocks the button.
  const getLockDisabledReason = () => {
    if (lockingCanvas) return "Locking the canvas…";
    if (canLockCanvas) return "";
    return "Available once every block is finalised.";
  };
  const lockDisabledReason = getLockDisabledReason();

  // Locking is terminal (Section 7.7) - there's no unlock endpoint - so it
  // goes through a confirmation popup instead of firing on the first click.
  // onLockCanvas is the same handler the parent already uses to call the
  // API and navigate on success; the popup only decides *when* that fires.
  const handleConfirmLock = () => {
    setShowLockConfirm(false);
    onLockCanvas();
  };

  // Agenda grouped in canvas order (band > section) - see
  // StrategyCanvasSummary.js. It's a reference list now: no ticking items off,
  // so no cleared count and no open/cleared filter to go with it. Progress is
  // read off the blocks on the left, which is where the decisions are made.
  //
  // Section and overall comments live in `remarks`, not on the layers - the
  // canvas on the left reads them from the same bucket the agenda does.
  const {
    sectionRemarksByLayer: sectionCommentsByLayer,
    // Canvas-wide remarks - the ones that matched no section.
    looseRemarks: overallRemarks,
  } = groupLiveReviewRemarks({ layers, remarks });

  const agendaGroups = buildAgendaGroups({ layers, remarks });
  const agendaTotal = countItems(agendaGroups);

  const openRemarksByBlock = useMemo(
    () => getOpenRemarksByBlock({ layers, remarks }),
    [layers, remarks],
  );

  // Clicking an agenda item reveals whatever it refers to on the canvas. The
  // target may be inside a folded band, and a folded band renders no body at
  // all, so the element usually doesn't exist yet at click time - we unfold
  // first, record what we're after, and let the effect below scroll once React
  // has actually put it in the DOM.
  const handleSelectAgendaItem = (item, group) => {
    if (group?.bandKey) {
      setCollapsedBands((prev) => {
        if (!prev.has(group.bandKey)) return prev; // already open
        const next = new Set(prev);
        next.delete(group.bandKey);
        return next;
      });
    }
    if (item.kind === ITEM_KIND.BLOCK) {
      setExpandedBlockKey(item.remark.block_key);
    }
    setPendingTarget(item.targetKey);
  };

  useEffect(() => {
    if (!pendingTarget) return;
    const node = canvasRef.current?.querySelector(
      `[data-agenda-target="${pendingTarget}"]`,
    );
    // Clear either way: a target that isn't on the canvas (a remark for a
    // block the payload no longer returns) shouldn't leave this armed and
    // re-fire on the next unrelated render.
    setPendingTarget(null);
    if (!node) return;

    node.scrollIntoView({ behavior: "smooth", block: "center" });

    // The scroll alone leaves the coach hunting for what moved, especially
    // when the row lands mid-canvas among identical-looking siblings.
    node.classList.add("lrs-target-flash");
    const timer = setTimeout(
      () => node.classList.remove("lrs-target-flash"),
      1600,
    );
    return () => clearTimeout(timer);
  }, [pendingTarget]);

  return (
    <div className="lrs-body">
      <div className="lrs-canvas" ref={canvasRef}>
        {bands.map((band) => (
          <CanvasBand
            key={band.key}
            label={band.label}
            color={band.color}
            // "x/y reviewed" belongs to the solo review pass. By the live
            // session every block has already been decided, so the bar would
            // read 4/4 on every band and say nothing. What's useful now is how
            // much of the band is still open, which is also what tells the
            // coach which folded band to go to next.
            showProgress={false}
            openCount={countOpenBlocks(band)}
            expanded={!collapsedBands.has(band.key)}
            onToggle={() => toggleBand(band.key)}
          >
            {band.sections.map((section) => (
              <div className="lrs-section" key={section.key}>
                {/* Growth / Core / DNA are single-section bands whose band
                    label already names them, so they skip this heading. */}
                {!section.hideHeading && (
                  <div className="lrs-section-heading">
                    <span className="lrs-section-bar" />
                    <span className="lrs-section-title">{section.title}</span>
                    {section.hint && (
                      <span className="lrs-section-hint">{section.hint}</span>
                    )}
                  </div>
                )}

                {/* The coach's read on the whole sub-section, above the blocks
                    it applies to. Sourced from `remarks` rather than from
                    layer.section_comments: the live-review payload doesn't
                    carry that field at all, which is why nothing rendered
                    here before. */}
                {(sectionCommentsByLayer.get(section.key) || [])
                  .filter((remark) => remark.comment_text?.trim())
                  .map((remark) => (
                    <div
                      className="lrs-section-comment"
                      data-agenda-target={`section:${section.key}`}
                      key={`${remark.remark_type}-${remark.id}`}
                    >
                      <span className="lrs-section-comment-badge">
                        Section comment
                      </span>
                      <p>{remark.comment_text}</p>
                    </div>
                  ))}

                <div className="cb-groups">
                  {section.groups.map((group, groupIndex) => (
                    <CanvasGroup
                      key={group.label || `group-${groupIndex}`}
                      label={group.label}
                    >
                      <div className="lrs-rows">
                        {group.blocks.map((block) => (
                          <LiveReviewRow
                            key={block.block_key}
                            briefId={briefId}
                            block={block}
                            openRemarks={
                              openRemarksByBlock.get(block.block_key) || []
                            }
                            disabled={!interactive}
                            expanded={
                              interactive && expandedBlockKey === block.block_key
                            }
                            onToggle={() =>
                              setExpandedBlockKey((prev) =>
                                prev === block.block_key ? null : block.block_key,
                              )
                            }
                          />
                        ))}
                      </div>
                    </CanvasGroup>
                  ))}
                </div>
              </div>
            ))}
          </CanvasBand>
        ))}

        {/* The overall assessment is about the canvas as a whole, so it closes
            the canvas out - after every section rather than pinned to one. */}
        {overallRemarks.filter((remark) => remark.comment_text?.trim()).length >
          0 && (
          <div className="lrs-overall" data-agenda-target="overall">
            <span className="lrs-overall-badge">Overall assessment</span>
            {overallRemarks
              .filter((remark) => remark.comment_text?.trim())
              .map((remark) => (
                <p
                  className="lrs-overall-text"
                  key={`${remark.remark_type}-${remark.id}`}
                >
                  {remark.comment_text}
                </p>
              ))}
          </div>
        )}
      </div>

      <div className="lrs-remarks">
        {/* No cleared count, no open/cleared chips: with nothing to tick off
            there's no progress here to report or filter by. */}
        <div className="lrs-remarks-header">Meeting Agenda</div>

        <div className="lrs-remarks-body">
          {agendaTotal === 0 ? (
            <div className="lrs-remarks-empty">
              <span className="lrs-remarks-empty-icon">
                <CheckIcon size={18} />
              </span>
              {/* Pre-start renders with remarks={[]} (the agenda only loads
                  once the session is live), so "you accepted every block"
                  would be untrue there even when blocks were flagged. */}
              <p className="lrs-remarks-empty-title">
                {interactive ? "Nothing to discuss" : "Agenda not loaded yet"}
              </p>
              <p className="lrs-remarks-empty-text">
                {interactive
                  ? "You accepted every block without flagging anything, so there are no agenda items for this session."
                  : "Your agenda appears here when the session starts."}
              </p>
            </div>
          ) : (
            <SummaryGroups
              groups={agendaGroups}
              onSelectItem={handleSelectAgendaItem}
            />
          )}
        </div>
        {interactive && (
          <div className="lrs-remarks-footer">
            {!canLockCanvas || lockingCanvas ? (
              <StandardTooltip tooltTipContent={lockDisabledReason}>
                <span className="lrs-lock-canvas-tooltip-wrap">
                  <button type="button" className="lrs-lock-canvas-btn" disabled>
                    {lockingCanvas ? "Locking…" : "Lock Canvas"}
                    <ChevronRightIcon />
                  </button>
                </span>
              </StandardTooltip>
            ) : (
              <button
                type="button"
                className="lrs-lock-canvas-btn"
                onClick={() => setShowLockConfirm(true)}
              >
                Lock Canvas
                <ChevronRightIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {showLockConfirm && (
        <ActionConfirmModal
          icon={<LockIcon size={20} />}
          // UAT (JK): the CEO owns the canvas and locks it with the coach, so
          // the copy frames this as a joint decision even though the coach
          // presses the button.
          title="Lock the canvas with the CEO?"
          subtitle="This makes it the CEO's final Strategy Canvas."
          text="Once locked, it can't be edited. Only lock it once you and the CEO have agreed every block."
          confirmLabel="Lock Canvas"
          onConfirm={handleConfirmLock}
          onCancel={() => setShowLockConfirm(false)}
        />
      )}
    </div>
  );
};

// Section 7.4 - the interactive Live Review session body, shown once
// canvas_state is LIVE_REVIEW_IN_PROGRESS.
const LiveReviewSessionBody = ({ briefId, onLockCanvas, lockingCanvas }) => {
  useEffect(() => {
    useStrategyCanvasReviewStore.getState().fetchLiveReview(briefId, { force: true });
  }, [briefId]);

  const liveReviewByUniqueId = useStrategyCanvasReviewStoreSelector(selectLiveReviewByUniqueId);
  const liveReviewLoading = useStrategyCanvasReviewStoreSelector(selectLiveReviewLoading);
  const liveReview = liveReviewByUniqueId[briefId];

  if (liveReviewLoading || !liveReview) {
    return <LiveReviewSkeleton />;
  }

  return (
    <LiveReviewBody
      briefId={briefId}
      layers={liveReview.layers}
      remarks={liveReview.remarks || []}
      interactive
      onLockCanvas={onLockCanvas}
      lockingCanvas={lockingCanvas}
    />
  );
};

// Pre-start (canvas_state === COACH_REVIEW_SUBMITTED) - shows the same
// canvas layout, sourced from the 5.2 review data (always available,
// unlike 7.4 which is gated to LIVE_REVIEW_IN_PROGRESS), but read-only -
// no expand/collapse, no remarks yet since those only come from 7.4.
const LiveReviewPreStartBody = ({ briefId }) => {
  useEffect(() => {
    useStrategyCanvasReviewStore.getState().fetchReview(briefId);
  }, [briefId]);

  const reviewByUniqueId = useStrategyCanvasReviewStoreSelector(selectReviewByUniqueId);
  const reviewLoading = useStrategyCanvasReviewStoreSelector(selectReviewLoading);
  const review = reviewByUniqueId[briefId];

  if (reviewLoading || !review) {
    return <LiveReviewSkeleton />;
  }

  return (
    <LiveReviewBody
      briefId={briefId}
      layers={review.layers}
      remarks={[]}
      interactive={false}
    />
  );
};

// Sections 7.2/7.3/7.4 - one screen. Until the coach clicks "Start Live
// Review" (7.3), no block/remark data is fetched or interactive - 7.4 is
// only callable once canvas_state is LIVE_REVIEW_IN_PROGRESS anyway. Once
// started, the header swaps to the Review Mode/Canvas Mode toggle (Figma
// 33450-155220), matching the solo Review screen's own toggle, and "Lock
// Canvas" moves down into the Meeting Agenda panel's footer.
const StrategyCanvasLiveReviewWaiting = () => {
  const history = useHistory();
  const { briefId } = useParams();
  const dispatch = useDispatch();
  const [mode, setMode] = useState(MODE_REVIEW);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const clients = useStrategyCanvasStoreSelector(selectClients);
  const client = (clients || []).find((sme) => sme.briefId === briefId);
  const companyName = client?.name || "";

  const statusByUniqueId = useStrategyCanvasReviewStoreSelector(selectLiveReviewStatusByUniqueId);
  const statusLoading = useStrategyCanvasReviewStoreSelector(selectLiveReviewStatusLoading);
  const startingLiveReview = useStrategyCanvasReviewStoreSelector(selectStartingLiveReview);
  const status = statusByUniqueId[briefId];

  const liveReviewByUniqueId = useStrategyCanvasReviewStoreSelector(selectLiveReviewByUniqueId);
  const liveReview = liveReviewByUniqueId[briefId];
  const lockingCanvas = useStrategyCanvasReviewStoreSelector(selectLockingCanvas);

  const isLiveReviewInProgress = status?.canvas_state === LIVE_REVIEW_IN_PROGRESS;

  // Blocks finalised, not agenda items cleared. Nothing marks an agenda item
  // discussed any more, so that count could only ever read 0 of N - and it's
  // the blocks that gate Lock Canvas, so this is the number worth watching.
  // Derived from the live layers rather than the payload's own counts so it
  // stays in sync with optimistic updates without waiting on a refetch.
  const allHeaderBlocks = (liveReview?.layers || []).flatMap(
    (layer) => layer.blocks || [],
  );
  const blocksTotal = allHeaderBlocks.length;
  const blocksFinalised = allHeaderBlocks.filter(
    (block) => (block.status || block.review_status) === REVIEW_STATUS.ACCEPTED,
  ).length;

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Live Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Strategy Canvas" });
    if (!useStrategyCanvasStore.getState().clients) {
      useStrategyCanvasStore.getState().fetchDashboard();
    }
    // Force a fresh fetch every time this screen is landed on - the coach
    // may return here repeatedly while waiting for the scheduled date.
    useStrategyCanvasReviewStore.getState().fetchLiveReviewStatus(briefId, { force: true });
    // eslint-disable-next-line
  }, [briefId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(STRATEGY_CANVAS_LIVE_REVIEW_WAITING, [
        breadCrumbsArray[STRATEGY_CANVAS_LIVE_REVIEW_WAITING][0],
        { title: companyName, url: STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId) },
        breadCrumbsArray[STRATEGY_CANVAS_LIVE_REVIEW_WAITING][2],
      ]),
    );
    // eslint-disable-next-line
  }, [companyName, briefId]);

  // First-time onboarding - pop the "how live review works" explainer open
  // on its own the first time a live review session actually starts for
  // this coach, instead of relying on them noticing the info affordance.
  useEffect(() => {
    if (isLiveReviewInProgress && !localStorage.getItem(LIVE_REVIEW_INFO_SEEN_KEY)) {
      setShowInfoModal(true);
      localStorage.setItem(LIVE_REVIEW_INFO_SEEN_KEY, "true");
    }
    // eslint-disable-next-line
  }, [isLiveReviewInProgress]);

  const handleStartLiveReview = async () => {
    const result = await useStrategyCanvasReviewStore.getState().startLiveReview(briefId);
    if (result) {
      useStrategyCanvasReviewStore.getState().fetchLiveReview(briefId, { force: true });
    }
  };

  const handleLockCanvas = async () => {
    const result = await useStrategyCanvasReviewStore.getState().lockCanvas(briefId);
    if (result) {
      history.push(STRATEGY_CANVAS_VIEW.replace(":briefId", briefId));
    }
  };

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId))}
        showBack
      />
      <div id="strategy-canvas-live-review-session">
        <div className="lrs-header">
          <div className="lrs-header-title">Strategy Canvas Review</div>
          {isLiveReviewInProgress ? (
            <div className="lrs-header-right">
              <button
                type="button"
                className="lrs-info-icon"
                onClick={() => setShowInfoModal(true)}
              >
                <InfoIcon size={14} />
                <span>How live review works</span>
              </button>
              {liveReview ? (
                <div className="lrs-agenda-count">
                  Blocks finalised:
                  <strong>
                    {" "}
                    {blocksFinalised} of {blocksTotal}
                  </strong>
                </div>
              ) : (
                <span className="lrs-agenda-count-skel" />
              )}
              <div className="lrs-mode-toggle">
                <button
                  type="button"
                  className={`lrs-mode-btn ${mode === MODE_REVIEW ? "active" : ""}`}
                  onClick={() => setMode(MODE_REVIEW)}
                >
                  Review Mode
                </button>
                <button
                  type="button"
                  className={`lrs-mode-btn ${mode === MODE_CANVAS ? "active" : ""}`}
                  onClick={() => setMode(MODE_CANVAS)}
                >
                  Canvas Mode
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="lrs-lock-canvas-btn"
              disabled={statusLoading || !status || startingLiveReview}
              onClick={handleStartLiveReview}
            >
              {startingLiveReview ? "Starting…" : "Start live review"}
              <ChevronRightIcon />
            </button>
          )}
        </div>
        <div className="lrs-header-divider" />

        {statusLoading || !status ? (
          <LiveReviewSkeleton />
        ) : isLiveReviewInProgress && mode === MODE_CANVAS ? (
          <StrategyCanvasCanvasMode briefId={briefId} companyName={companyName} />
        ) : isLiveReviewInProgress ? (
          <LiveReviewSessionBody
            briefId={briefId}
            onLockCanvas={handleLockCanvas}
            lockingCanvas={lockingCanvas}
          />
        ) : (
          <LiveReviewPreStartBody briefId={briefId} />
        )}
      </div>

      {showInfoModal && <LiveReviewInfoModal onClose={() => setShowInfoModal(false)} />}
    </div>
  );
};

export default StrategyCanvasLiveReviewWaiting;
